import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";
import { Loader2, ShieldCheck, Layers, Users2 } from "lucide-react";

/**
 * Enterprise login: a responsive two-pane layout. On lg+ a branded
 * marketing/trust panel sits beside the form; below lg the panel is
 * hidden and the form fills the screen (mobile-first). Uses the shared
 * TIS BrandLogo and a password field with a show/hide toggle.
 */
export default function Login() {
  const { login, user, isPlatformOperator } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={isPlatformOperator() ? "/platform" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = await login(email, password);
      const roles = payload.roles || [];
      const isOperator = roles.some((r) => r.code === "P-SA" || r.code === "P-OPS");
      navigate(isOperator ? "/platform" : "/admin");
    } catch (err) {
      setError(errorMessage(err) || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Brand / trust panel — hidden on small screens. A professional
          background photo (self-hosted in public/images) replaces the old
          flat gradient; a dark gradient overlay sits over it so the white
          heading, body copy and feature chips keep AA contrast on the image. */}
      <aside
        className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex"
        style={{
          backgroundImage: "url(/images/login-bg.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-900/70 to-slate-900/85"
          aria-hidden="true"
        />
        <div className="relative rounded-lg bg-white/95 px-4 py-3 shadow-sm w-fit">
          <BrandLogo size="md" />
        </div>
        <div className="relative max-w-md">
          <h2 className="font-heading text-3xl font-bold leading-tight">One platform for every portal, every tenant.</h2>
          <p className="mt-3 text-white/80">
            Manage branding, billing, access and portal configuration across your Talent and Hub experiences — from a single, secure control plane.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: Layers, t: "Multi-portal by design", s: "Talent & Hub share one branded identity." },
              { icon: Users2, t: "Role-based access", s: "Per-portal entitlements via the central Auth platform." },
              { icon: ShieldCheck, t: "Enterprise security", s: "Tenant-isolated data, audited and SSO-ready." },
            ].map(({ icon: Icon, t, s }) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span>
                  <span className="block font-semibold">{t}</span>
                  <span className="block text-sm text-white/70">{s}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/50">© {new Date().getFullYear()} TIS. All rights reserved.</p>
      </aside>

      {/* Form pane */}
      <main className="flex w-full flex-col items-center justify-center px-6 py-10 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center lg:hidden">
            <BrandLogo size="lg" />
          </div>
          <h1 className="text-center font-heading text-2xl font-bold text-foreground sm:text-left">Sign in</h1>
          <p className="mt-1 text-center text-sm text-muted-foreground sm:text-left">
            Tenant admin or platform operator access.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <a href="/recovery" className="text-xs font-medium text-primary hover:underline">Forgot password?</a>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div data-testid="login-error" role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New company?{" "}
            <a href="/signup" className="font-medium text-primary hover:underline">Start your free trial</a>
          </p>
        </div>
      </main>
    </div>
  );
}
